import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useQuery } from 'react-fetching-library';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, Typography, TextField, Button, Modal, Card, CardContent, CardHeader, Checkbox, Alert, LinearProgress, IconButton, Tooltip, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import Iconify from '../../../components/Iconify';
import CloseIcon from '@mui/icons-material/Close';

import OrdersModal from './OrdersModal';
import SendMessageResultModal from './SendMessageResultModal';
import { getNotificationSettingsAction, sendNotificationsAction } from '../../../services/request';
import { setAllowAutoOrderUpdate } from '../../../services/redux-actions';
import RichTextEditor from '../notification/RichTextEditor';
import { SmallFormTextField } from '../../../components/small-fields/SmallFormTextField';
import { EmailEditorImpl } from '../notification/EmailEditorImpl';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

const EmailMessageEditor = ({ formik, disabledEditorChange }) => {
  const {
    send_email,
    send_any_email_message,
    text_editor_mode,
    subject
  } = formik.values;

  if (!send_email && !send_any_email_message) {
    return null;
  }

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" mr={2} mb={1}>Tryb edytora tekstu</Typography>
        <RadioGroup
          row
          value={formik.values.text_editor_mode}
          onChange={e => formik.setFieldValue('text_editor_mode', e.target.value)}
        >
          <FormControlLabel
            value="rich_text_editor"
            control={<Radio />}
            label="Podstawowy"
            disabled={disabledEditorChange}
          />
          <FormControlLabel
            value="email_editor"
            control={<Radio />}
            label="Rozszerzony"
            disabled={disabledEditorChange}
          />
        </RadioGroup>
      </Stack>
      <TextField
        id="subject"
        name="subject"
        label="Temat e-mail"
        value={subject}
        onChange={formik.handleChange}
        error={formik.errors.subject && formik.touched.subject}
        helperText={formik.errors.subject}
      />

      <Box height={20} />

      {text_editor_mode === 'rich_text_editor' ? (
        <RichTextEditor
          text={formik.values.email_message_content}
          formik={formik}
          fieldName="email_message_content"
          label={null}
          placeholder="Treść wiadomości e-mail"
          helperText={formik.errors.email_message_content}
          error={formik.errors.email_message_content && formik.touched.email_message_content}
          height="220px"
        />
      ) : (
        <EmailEditorImpl
          formik={formik}
          fieldName="email_message_content"
          design={formik.values.email_editor_design_object}
        />
      )}
    </>
  );
};

const SmsMessageEditor = ({ formik }) => {
  const {
    send_sms,
    send_sms_message_any_number,
    sms_message_content
  } = formik.values;

  if (!send_sms && !send_sms_message_any_number) {
    return null;
  }

  return (
    <TextField
      id="sms_message_content"
      name="sms_message_content"
      multiline
      rows={8}
      label="Treść wiadomości SMS"
      value={sms_message_content}
      onChange={formik.handleChange}
      error={formik.errors.sms_message_content && formik.touched.sms_message_content}
      helperText={formik.errors.sms_message_content}
    />
  );
};

const validationSchema = Yup.object({
  send_email: Yup.boolean(),
  send_sms: Yup.boolean(),
  send_sms_message_any_number: Yup.boolean(),
  send_any_email_message: Yup.boolean(),
  email_message_content: Yup.string().when(
    ['send_email', 'send_any_email_message'],
    {
      is: (sendEmail, sendAnyEmail) => sendEmail || sendAnyEmail,
      then: Yup.string().required('To pole nie może być puste')
    }
  ),

  sms_message_content: Yup.string().when(
    ['send_sms', 'send_sms_message_any_number'],
    {
      is: (sendSms, sendSmsAnyNumber) => sendSms || sendSmsAnyNumber,
      then: Yup.string().required('To pole nie może być puste')
    }
  ),
  subject: Yup.string()
    .when('send_email', {
      is: true,
      then: Yup.string().required('To pole nie może być puste')
    })
    .when('send_any_email_message', {
      is: true,
      then: Yup.string().required('To pole nie może być puste')
    }),
  any_numbers: Yup.string().when('send_sms_message_any_number', {
    is: true,
    then: Yup.string().required('To pole nie może być puste')
  }),
  any_emails: Yup.string().when('send_any_email_message', {
    is: true,
    then: Yup.string().required('To pole nie może być puste')
  })
});

export default function SendMessageL27({ idList, setIdList, ordersDataForMessages }) {
  const dispatch = useDispatch();
  const { mutate, error, loading, reset } = useMutation(sendNotificationsAction);
  const { loading: settingsLoading, payload: settingsPayload, error: settingsError } = useQuery(getNotificationSettingsAction());
  const { module, seasonYear } = useSelector(state => state.app);
  const [open, setOpen] = React.useState(false);
  const [openOrdersModal, setOpenOrdersModal] = React.useState(false);
  const [openResultModal, setOpenResultModal] = React.useState(false);
  const [result, setResult] = React.useState({});
  const [notificationSettings, setNotificationSettings] = React.useState([]);
  const [fullNotificationSettings, setFullNotificationSettings] = React.useState([]);
  const [disabledEditorChange, setDisabledEditorChange] = React.useState(false);
  const [disabledNotificationSettings, setDisabledNotificationSettings] = React.useState(false);

  const handleOpen = () => {
    dispatch(setAllowAutoOrderUpdate(false));
    setOpen(true);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
    setIdList(ordersDataForMessages.map(order => order.id));
    dispatch(setAllowAutoOrderUpdate(true));
    setDisabledEditorChange(false);
    formik.resetForm();
  };

  const handleOpenOrdersModal = () => setOpenOrdersModal(true);
  const handleCloseOrdersModal = () => setOpenOrdersModal(false);

  const handleOpenResultModal = () => setOpenResultModal(true);
  const handleCloseResultModal = () => setOpenResultModal(false);

  const handleSubmit = async values => {
    const { error: mutateError, payload } = await mutate(values);
    if (!mutateError) {
      setResult(payload);
      handleOpenResultModal();
      formik.resetForm();
    }
  };

  const formik = useFormik({
    initialValues: {
      send_email: false,
      send_sms: false,
      send_sms_message_any_number: false,
      send_any_email_message: false,
      email_message_content: '',
      sms_message_content: '',
      subject: '',
      any_numbers: '',
      any_emails: '',
      email_editor_design_object: '',
      notification_settings: null,
      text_editor_mode: 'rich_text_editor'
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: values => {
      handleSubmit({
        ...values,
        orders_id: idList,
        module_identifier: module,
        season_year: seasonYear
      });
    }
  });


  const handleNotificationSettingsChange = value => {
    formik.setFieldValue('notification_settings', value);
    if (value == null) {
      setDisabledEditorChange(false);
      formik.setFieldValue('email_editor_design_object', '<></>');
      formik.setFieldValue('email_message_content', '');
      formik.setFieldValue('subject', '');
      formik.setFieldValue('sms_message_content', '');

      return;

    }
    const fullData = fullNotificationSettings.find(n => n.id == value);
    if (!fullData) {
      return;
    }
    if (formik.values.send_email || formik.values.send_any_email_message) {
      if (fullData.show_in_email_editor) {
        formik.setFieldValue('text_editor_mode', 'email_editor');
      } else {
        formik.setFieldValue('text_editor_mode', 'rich_text_editor');
      }
      formik.setFieldValue('email_editor_design_object', fullData.email_editor_design_object);
      formik.setFieldValue('email_message_content', fullData.email_message);
      formik.setFieldValue('subject', fullData.email_subject);
    }
    if (formik.values.send_sms || formik.values.send_sms_message_any_number) {
      formik.setFieldValue('sms_message_content', fullData.sms_message);
    }
    setDisabledEditorChange(true);

  };

  React.useEffect(() => {
    if (!settingsError && !settingsLoading) {
      setFullNotificationSettings(settingsPayload.results);
      setNotificationSettings([
        {
          value: null,
          label: '-'
        },
        ...settingsPayload.results.map(item => ({
          value: item.id,
          label: item.pl_type_name
        }))
      ]);
    }
  }, [settingsLoading]);

  return (
    <Stack>
      <Box sx={{ p: 3, width: '100%', display: 'flex', flexDirection: 'row' }}>
        <Button variant="contained" fullWidth endIcon={<EmailIcon />} onClick={handleOpen}>Wyślij</Button>
      </Box>
      <Modal
        open={open}
        onClose={handleClose}
      >
        <Card sx={{ ...style, width: '90vw', overflow: 'auto', maxHeight: '100svh' }}>
          <CardHeader
            title="Wyślij wiadomość"
            action={
              <>
                <Tooltip title="lista zamówień">
                  <IconButton color="primary" onClick={handleOpenOrdersModal}>
                    <Iconify icon="icon-park-outline:transaction-order" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="zamknij">
                  <IconButton color="primary" onClick={handleClose}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </>
            }
            sx={{ textAlign: 'center' }}
          />
          <CardContent>
            <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
              <Stack direction="column" spacing={4}>
                <Stack direction="row" justifyContent="start">
                  <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                    <Checkbox id="send_email" name="send_email" checked={formik.values.send_email} onChange={formik.handleChange} />
                    <Typography variant="subtitle2">Wiadomość E-mail</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                    <Checkbox id="send_sms" name="send_sms" checked={formik.values.send_sms} onChange={formik.handleChange} />
                    <Typography variant="subtitle2">Wiadomość SMS</Typography>
                  </Stack>
                </Stack>
                <Stack direction="row">
                  <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                    <Checkbox id="send_any_email_message" name="send_any_email_message" checked={formik.values.send_any_email_message} onChange={e => {
                      formik.handleChange(e);
                      if (e.target.checked === true) {
                        setIdList([]);
                        setDisabledNotificationSettings(true);
                        handleNotificationSettingsChange(null);
                      } else {
                        setDisabledNotificationSettings(false);
                      }
                    }} />
                    <Typography variant="subtitle2">Wiadomość do dowolnych adresów e-mail</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="left" spacing={1}>
                    <Checkbox id="send_sms_message_any_number" name="send_sms_message_any_number" checked={formik.values.send_sms_message_any_number} onChange={e => {
                      formik.handleChange(e);
                      if (e.target.checked === true) {
                        setIdList([]);
                      }
                    }} />
                    <Typography variant="subtitle2">Wiadomość SMS do dowolnych numerów</Typography>
                  </Stack>
                </Stack>
              </Stack>
              <Box height={20}/>
              {formik.values.send_any_email_message
                ? <>
                  <TextField
                    id="any_emails"
                    name="any_emails"
                    multiline
                    rows={6}
                    label="Dowolne emaile"
                    value={formik.values.any_emails}
                    onChange={formik.handleChange}
                    error={formik.errors.any_emails && formik.touched.any_emails}
                    helperText={formik.errors.any_emails ? formik.errors.any_emails : 'Każdy email od nowej linii'}
                    sx={{ mb: '20px' }}
                  />
                </>
                : null}
              {formik.values.send_sms_message_any_number
                ? <>
                  <TextField
                    id="any_numbers"
                    name="any_numbers"
                    multiline
                    rows={6}
                    label="Dowolne numery"
                    value={formik.values.any_numbers}
                    onChange={formik.handleChange}
                    error={formik.errors.any_numbers && formik.touched.any_numbers}
                    helperText={formik.errors.any_numbers ? formik.errors.any_numbers : 'Każdy numer od nowej linii'}
                  />
                </>
                : null}
              <Stack direction="row" justifyContent="left" spacing={1} mb={2}>
                <Typography variant="subtitle2" pt={2}>Szablon powiadomienia</Typography>
                <SmallFormTextField
                  select
                  id="notification_settings"
                  name="notification_settings"
                  options={notificationSettings}
                  value={formik.values.notification_settings}
                  onChange={e => handleNotificationSettingsChange(e.target.value)}
                  error={formik.touched.notification_settings && Boolean(formik.errors.notification_settings)}
                  helperText={<Typography color="error" variant="caption" fontWeight={700}>Uwaga! Niektóre szablony mają dodatkowe warunki wysyłki, np. zależne od statusu zamówienia. W takim przypadku wiadomość może nie zostać wysłana do wszystkich wybranych zamówień.</Typography>}
                  disabled={disabledNotificationSettings}
                />
              </Stack>
              <EmailMessageEditor
                formik={formik}
                disabledEditorChange={disabledEditorChange}
              />
              <Box height={10} />
              <SmsMessageEditor formik={formik} />

              { error
                ? <>
                  <Box height={40}/>
                  <Alert variant="outlined" severity="error">Nie udało się wysłać wiadomości</Alert>
                </>
                : null}
              <Box height={40}/>
              { loading
                ? <>
                  <LinearProgress color="success" />
                </>
                : <Button disabled={!formik.values.send_email && !formik.values.send_sms && !formik.values.send_sms_message_any_number && !formik.values.send_any_email_message} type="submit" variant="contained">
                  Wyślij
                </Button>}
            </form>
          </CardContent>
        </Card>
      </Modal>
      <OrdersModal
        open={openOrdersModal}
        handleClose={handleCloseOrdersModal}
        idList={idList}
        setIdList={setIdList}
        ordersDataForMessages={ordersDataForMessages}
      />
      <SendMessageResultModal open={openResultModal} handleClose={handleCloseResultModal} result={result} />
    </Stack>
  );
}
